import React from 'react';
import {observable, action, makeObservable, runInAction} from 'mobx';
import {observer} from 'mobx-react';
import s from '../s';
import ModalSimple from '../../global/modal-simple';
import {add_to_cart} from '../api';
import InfoIcon from './shared/info-icon';
import ATCButtonTemplate from './atc/atc-button-template';
import {modal, Portal} from '../../util';

declare const collapse: {
    init: () => void;
};

@observer
export default class CAFDocSize extends React.Component {
    @observable doc1_w: number;
    @observable doc1_h: number;
    @observable doc2_w: number;
    @observable doc2_h: number;
    @observable print1_w: number;
    @observable print1_h: number;
    @observable print2_w: number;
    @observable print2_h: number;

    @observable isHorizontal: string;
    @observable notes: string;
    @observable loading: boolean;
    @observable debounceTimeout: NodeJS.Timeout | null = null;

    @observable error1: string = '';
    @observable error2: string = '';
    @observable error3: string = '';
    @observable error4: string = '';

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    componentDidMount(): void {
        if (s.ps.is_horizontal) {
            this.isHorizontal = '1';
        } else {
            this.isHorizontal = '0';
        }
    }

    componentWillUnmount() {
        this.clearTimeout();
    }

    @action
    stop_caf = async () => {
        s.ui.show_caf_doc_sizes = false;
        s.ui.show_caf_doc_sizes_both = false;
        setTimeout(function () {
            collapse.init();
        }, 150);
    };

    @action
    atc = async (renderFrame: boolean = false) => {
        this.error1 = '';
        this.error2 = '';
        this.error3 = '';
        this.error4 = '';
        if (s.prod.DYF_FrameStyleID === 2 && s.ui.show_caf_doc_sizes_both) {
            if (!this.doc2_w) {
                this.error3 = `Invalid bottom ${s.prod.DocType.toLowerCase()} width`;
                return;
            }
            if (!this.doc2_h) {
                this.error3 = `Invalid bottom ${s.prod.DocType.toLowerCase()} height`;
                return;
            }
            if (this.print2_w && this.print2_w < 1) {
                this.error2 = `Bottom printed width is too low. Must be greater than 1.`;
            }
            if (this.print2_h && this.print2_h < 1) {
                this.error2 = `Bottom printed height is too low. Must be greater than 1.`;
            }
            if (this.print2_w && this.print2_w > 30) {
                this.error4 = `Bottom printed width is too high. Must be less than 30 inches.`;
            }
            if (this.print2_h && this.print2_h > 24) {
                this.error4 = `Bottom printed height is too high. less than 24 inches.`;
            }
            if (this.print2_w && this.print2_w > this.doc2_w) {
                this.error2 = `Bottom printed width is too high. Must be less than bottom ${s.prod.DocType.toLowerCase()} width.`;
            }
            if (this.print2_h && this.print2_h > this.doc2_h) {
                this.error2 = `Bottom printed height is too high. Must be less than bottom ${s.prod.DocType.toLowerCase()} height.`;
            }

            if (this.doc2_w < 1) {
                this.error3 = `Bottom ${s.prod.DocType} width is too low. Must be greater than 1.`;
            }
            if (this.doc2_h < 1) {
                this.error3 = `Bottom ${s.prod.DocType} height is too low. Must be greater than 1.`;
            }
            if (this.doc2_w > 30) {
                this.error3 = `Bottom ${s.prod.DocType} width is too high. Must be less than 30 inches.`;
            }
            if (this.doc2_h > 24) {
                this.error3 = `Bottom ${s.prod.DocType} height is too high. Must be less than 24 inches.`;
            }
        }

        if (!this.doc1_w) {
            this.error1 = `Invalid ${s.prod.DocType.toLowerCase()} width`;
            return;
        }
        if (!this.doc1_h) {
            this.error1 = `Invalid ${s.prod.DocType.toLowerCase()} height`;
            return;
        }
        if (this.doc1_w < 1) {
            this.error1 = `${s.prod.DocType} width is too low. Must be greater than 1.`;
        }
        if (this.doc1_h < 1) {
            this.error1 = `${s.prod.DocType} height is too low. Must be greater than 1.`;
        }
        if (this.doc1_w > 30) {
            this.error1 = `${s.prod.DocType} width is too high. Must be less than 30 inches.`;
        }
        if (this.doc1_h > 24) {
            this.error1 = `${s.prod.DocType} height is too high. Must be less than 24 inches.`;
        }

        if (this.print1_w && this.print1_w < 1) {
            this.error2 = `Printed width is too low. Must be greater than 1.`;
        }
        if (this.print1_h && this.print1_h < 1) {
            this.error2 = `Printed height is too low. Must be greater than 1.`;
        }
        if (this.print1_w && this.print1_w > 30) {
            this.error2 = `Printed width is too high. Must be less than 30 inches.`;
        }
        if (this.print1_h && this.print1_h > 24) {
            this.error2 = `Printed height is too high. Must be less than 24 inches.`;
        }

        if (this.print1_w && this.print1_w > this.doc1_w) {
            this.error2 = `Printed width is too high. Must be less than ${s.prod.DocType.toLowerCase()} width.`;
        }
        if (this.print1_h && this.print1_h > this.doc1_h) {
            this.error2 = `Printed height is too high. Must be less than ${s.prod.DocType.toLowerCase()} height.`;
        }

        if (this.error1 !== '' || this.error2 !== '' || this.error3 !== '' || this.error4 !== '') {
            return;
        }

        s.ps.docWidth = this.doc1_w;
        s.ps.docHeight = this.doc1_h;
        s.ps.printWidth = this.print1_w;
        s.ps.printHeight = this.print1_h;

        if (s.prod.DYF_FrameStyleID === 2 && s.ui.show_caf_doc_sizes_both) {
            s.ps.docWidth2 = this.doc2_w;
            s.ps.docHeight2 = this.doc2_h;
            s.ps.printWidth2 = this.print2_w;
            s.ps.printHeight2 = this.print2_h;
        } else if (s.prod.DYF_FrameStyleID === 2) {
            s.ps.docWidth2 = this.doc1_w;
            s.ps.docHeight2 = this.doc1_h;
            s.ps.printWidth2 = this.print1_w;
            s.ps.printHeight2 = this.print1_h;
        }

        this.isHorizontal = s.ps.docWidth >= s.ps.docHeight ? '1' : '0';

        s.ps.caf_note_IsHorizontal = this.isHorizontal === '1' ? true : false;
        if (this.notes && this.notes.length > 2) {
            s.ps.caf_note = this.notes.truncate(400, true);
        }

        if (renderFrame) {
            this.renderFrame();
        } else {
            this.loading = true;

            try {
                await add_to_cart();
            } catch (err) {
                console.error(err);
            } finally {
                runInAction(() => {
                    this.loading = false;
                });
            }
        }
    };

    @action
    renderFrame() {
        this.clearTimeout();
        this.debounceTimeout = setTimeout(() => {
            if (!s.ps.docWidth || !s.ps.docHeight) {
                return;
            }

            s.ui.doRender = true;
            s.ui.update_pricing = true;
            s.pulse();
        }, 1000);
    }

    @action
    clearTimeout() {
        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout);
        }
    }

    render() {
        return (
            <div className="caf-atc card-m">
                <div>Add To Cart</div>

                <div className="text-left">
                    <ModalSimple
                        id="modal-caf-atc-1"
                        title={'Measurement Table'}
                    >
                        <div>
                            <p>Use the table below to convert from fractions to decimal:</p>
                            <picture>
                                <source
                                    type="image/webp"
                                    srcSet={`https://${Portal?.CDNDomain}/images/dyf_help_decimals.webp`}
                                />
                                <img
                                    alt="Measurement table"
                                    loading="lazy"
                                    src={`https://${Portal?.CDNDomain}/images/dyf_help_decimals.png`}
                                />
                            </picture>
                        </div>
                    </ModalSimple>
                    <ModalSimple
                        id="modal-caf-atc-2"
                        title={'How to measure your Document'}
                    >
                        <div>
                            <p>How to measure your Document</p>
                            <picture>
                                <source
                                    type="image/webp"
                                    srcSet={`https://${Portal?.CDNDomain}/images/cached/general/how_to_measure_document.webp`}
                                />
                                <img
                                    alt="Measurement diagram"
                                    loading="lazy"
                                    src={`https://${Portal?.CDNDomain}/images/cached/general/how_to_measure_document.png`}
                                    className="resp"
                                />
                            </picture>
                        </div>
                    </ModalSimple>

                    <div className="text-left">
                        <p className="mt-2">
                            We require the actual paper size measurements to confirm price and accurately size your frame. Please convert fractions to decimals.{' '}
                            <InfoIcon
                                id="modal-caf-atc-1"
                                ariaLabel="View a photo of an inches to decimals sizing chart"
                            />
                        </p>
                        <p className="mt-3 text-center">
                            <button
                                type="button"
                                className="btn btn-outline-dark"
                                onClick={() => modal('modal-caf-atc-2')}
                                aria-haspopup="dialog"
                                aria-label="How to measure your Document"
                            >
                                How to measure your {s.prod.DocType}
                                <span style={{paddingLeft: '1rem'}}>
                                    <i className="far fa-info-circle" />
                                </span>
                            </button>
                        </p>
                        <hr className="mt-4" />

                        <div className="text-left">
                            {s.prod.DYF_FrameStyleID === 2 && !s.ui.show_caf_doc_sizes_both && <div className="subh">Top &amp; Bottom {s.prod.DocType}s</div>}
                            {s.prod.DYF_FrameStyleID === 2 && s.ui.show_caf_doc_sizes_both && <div className="subh">Top {s.prod.DocType}</div>}
                            <div className="mt-3">
                                <div
                                    className="header-std"
                                    style={{paddingRight: '0.25rem'}}
                                >
                                    1. Actual {s.prod.DocType} Size*:{' '}
                                </div>
                                <div
                                    className="input-group input-group-sm"
                                    style={{marginLeft: 15}}
                                >
                                    <div
                                        className="form-floating"
                                        style={{width: 125, flex: 'inherit'}}
                                    >
                                        <input
                                            type="number"
                                            step="0.125"
                                            name="doc1_w"
                                            className="form-control"
                                            placeholder="Width"
                                            style={{width: 125}}
                                            onChange={e => {
                                                this.doc1_w = parseFloat(e.target.value);
                                                this.atc(true);
                                            }}
                                            aria-label="Top Document Width in Inches"
                                        />
                                        <label htmlFor="doc1_w">Width</label>
                                    </div>
                                    <span className="input-group-text">x</span>
                                    <div
                                        className="form-floating"
                                        style={{width: 125, flex: 'inherit'}}
                                    >
                                        <input
                                            type="number"
                                            step="0.125"
                                            name="doc1_h"
                                            className="form-control"
                                            placeholder="Height"
                                            style={{width: 125}}
                                            onChange={e => {
                                                this.doc1_h = parseFloat(e.target.value);
                                                this.atc(true);
                                            }}
                                            aria-label="Top Document Height in Inches"
                                        />
                                        <label htmlFor="doc1_h">Height</label>
                                    </div>
                                    <span className="input-group-text">inches</span>
                                </div>
                                <div
                                    className="hint"
                                    style={{marginLeft: 15}}
                                >
                                    e.g. 10.5" W x 8" H
                                </div>
                                {this.error1 && this.error1 !== '' && <div className="err">{this.error1}</div>}
                            </div>
                            <div className="mt-3">
                                <div
                                    className="header-std"
                                    style={{paddingRight: '0.25rem'}}
                                >
                                    2. Printed Area Size (optional):{' '}
                                </div>
                                <div
                                    className="input-group input-group-sm"
                                    style={{marginLeft: 15}}
                                >
                                    <div
                                        className="form-floating"
                                        style={{width: 125, flex: 'inherit'}}
                                    >
                                        <input
                                            type="number"
                                            step="0.125"
                                            name="print1_w"
                                            className="form-control"
                                            placeholder="Width"
                                            style={{width: 125}}
                                            onChange={e => (this.print1_w = parseFloat(e.target.value))}
                                            aria-label="Top Document Width in Inches"
                                        />
                                        <label htmlFor="print1_w">Width</label>
                                    </div>
                                    <span className="input-group-text">x</span>
                                    <div
                                        className="form-floating"
                                        style={{width: 125, flex: 'inherit'}}
                                    >
                                        <input
                                            type="number"
                                            step="0.125"
                                            name="print1_h"
                                            className="form-control"
                                            placeholder="Height"
                                            style={{width: 125}}
                                            onChange={e => (this.print1_h = parseFloat(e.target.value))}
                                            aria-label="Top Document Height in Inches"
                                        />
                                        <label htmlFor="print1_h">Height</label>
                                    </div>
                                    <span className="input-group-text">inches</span>
                                </div>
                                <div
                                    className="hint"
                                    style={{marginLeft: 15}}
                                >
                                    e.g. 10.5" W x 8" H
                                </div>
                                {this.error2 && this.error2 !== '' && <div className="err">{this.error2}</div>}
                            </div>

                            {s.prod.DYF_FrameStyleID === 2 && !s.ui.show_caf_doc_sizes_both && (
                                <div className="mt-3">
                                    <p>Does your bottom {s.prod.DocType.toLowerCase()} have different dimensions?</p>
                                    <div style={{marginLeft: 15}}>
                                        <button
                                            type="button"
                                            onClick={() => (s.ui.show_caf_doc_sizes_both = true)}
                                            className="btn btn-outline-dark btn-sm"
                                        >
                                            Yes
                                        </button>
                                    </div>
                                </div>
                            )}

                            {s.prod.DYF_FrameStyleID === 2 && s.ui.show_caf_doc_sizes_both && <div className="subh mt-4">Bottom {s.prod.DocType}</div>}
                            {/* {s.prod.DYF_FrameStyleID === 2 && s.ui.show_caf_doc_sizes_both && <p className='hint' style={{marginLeft:15}}>Bottom {s.prod.DocType} information may be left blank if both {s.prod.DocType}s have identical dimensions.</p>} */}
                            {s.prod.DYF_FrameStyleID === 2 && s.ui.show_caf_doc_sizes_both && (
                                <div className="mt-3">
                                    <div
                                        className="header-std"
                                        style={{paddingRight: '0.25rem'}}
                                    >
                                        1. Actual {s.prod.DocType} Size*:{' '}
                                    </div>
                                    <div
                                        className="input-group input-group-sm"
                                        style={{marginLeft: 15}}
                                    >
                                        <div
                                            className="form-floating"
                                            style={{width: 125, flex: 'inherit'}}
                                        >
                                            <input
                                                type="number"
                                                name="doc2_w"
                                                className="form-control"
                                                placeholder="Width"
                                                style={{width: 125}}
                                                onChange={e => (this.doc2_w = parseFloat(e.target.value))}
                                                step="0.125"
                                                maxLength={7}
                                                aria-label="Bottom Document Width in Inches"
                                            />
                                            <label htmlFor="doc2_w">Width</label>
                                        </div>
                                        <span className="input-group-text">x</span>
                                        <div
                                            className="form-floating"
                                            style={{width: 125, flex: 'inherit'}}
                                        >
                                            <input
                                                type="number"
                                                name="doc2_h"
                                                className="form-control"
                                                placeholder="Height"
                                                style={{width: 125}}
                                                onChange={e => (this.doc2_h = parseFloat(e.target.value))}
                                                step="0.125"
                                                maxLength={7}
                                                aria-label="Bottom Document Height in Inches"
                                            />
                                            <label htmlFor="doc2_h">Height</label>
                                        </div>
                                        <span className="input-group-text">inches</span>
                                    </div>
                                    {this.error3 && this.error3 !== '' && <div className="err">{this.error3}</div>}
                                </div>
                            )}
                            {s.prod.DYF_FrameStyleID === 2 && s.ui.show_caf_doc_sizes_both && (
                                <div className="mt-3">
                                    <div
                                        className="header-std"
                                        style={{paddingRight: '0.25rem'}}
                                    >
                                        2. Printed Area Size (optional):{' '}
                                    </div>
                                    <div
                                        className="input-group input-group-sm"
                                        style={{marginLeft: 15}}
                                    >
                                        <div
                                            className="form-floating"
                                            style={{width: 125, flex: 'inherit'}}
                                        >
                                            <input
                                                type="number"
                                                name="print2_w"
                                                className="form-control"
                                                placeholder="Width"
                                                style={{width: 125}}
                                                onChange={e => (this.print2_w = parseFloat(e.target.value))}
                                                step="0.125"
                                                maxLength={7}
                                                aria-label="Bottom Document Width in Inches"
                                            />
                                            <label htmlFor="print2_w">Width</label>
                                        </div>
                                        <span className="input-group-text">x</span>
                                        <div
                                            className="form-floating"
                                            style={{width: 125, flex: 'inherit'}}
                                        >
                                            <input
                                                type="number"
                                                name="print2_h"
                                                className="form-control"
                                                placeholder="Height"
                                                style={{width: 125}}
                                                onChange={e => (this.print2_h = parseFloat(e.target.value))}
                                                step="0.125"
                                                maxLength={7}
                                                aria-label="Bottom Document Height in Inches"
                                            />
                                            <label htmlFor="print2_h">Height</label>
                                        </div>
                                        <span className="input-group-text">inches</span>
                                    </div>
                                    {this.error4 && this.error4 !== '' && <div className="err">{this.error4}</div>}
                                </div>
                            )}

                            <hr />

                            <div className="mt-3">
                                <div
                                    className="header-std"
                                    style={{paddingRight: '0.25rem'}}
                                >
                                    {s.prod.DocType} Orientation &amp; Notes:{' '}
                                </div>
                                <select
                                    className="form-select"
                                    aria-label="Select document orientation"
                                    value={this.isHorizontal}
                                    onChange={e => (this.isHorizontal = e.target.value)}
                                >
                                    <option value="1">Horizontal / Landscape</option>
                                    <option value="0">Vertical / Portrait</option>
                                </select>
                            </div>
                            <div className="mt-2 d-none">
                                <div
                                    className="form-floating"
                                    style={{}}
                                >
                                    <textarea
                                        name="caf_notes"
                                        maxLength={400}
                                        style={{height: 65}}
                                        onChange={e => (this.notes = e.target.value)}
                                        className="form-control"
                                        placeholder="Notes"
                                        aria-label="Notes for your order"
                                    />
                                    <label htmlFor="caf_notes">Notes For Us</label>
                                </div>
                            </div>
                            <hr />
                            <div className="text-center mb-2">
                                {s.ps && s.ps.atc_error && s.ps.atc_error !== '' && <div className="err mb-4">{s.ps.atc_error}</div>}
                                <div
                                    className="btn-group"
                                    role="group"
                                >
                                    <button
                                        type="button"
                                        className="btn btn-outline-dark"
                                        aria-label="Back to frame designer"
                                        onClick={this.stop_caf}
                                    >
                                        Back to Design
                                    </button>
                                    <ATCButtonTemplate
                                        loading={this.loading}
                                        clicked={() => this.atc()}
                                        additionalClass="btn"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
